import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ModalBody,
  ModalFooter,
  ModalPrimaryButton,
  ModalSecondaryButton,
  ModalWrapper,
} from '@zooplus/ui-components';

import { authorizePayment } from '../../../services/braintreeService';
import { initiateCreditCardPayment } from '../../../services/paymentService';
import { captureException } from '../../../services/sentryService';
import { removeDecimal } from '../../../utils/utils';
import { useAppContext } from '../../AppContextProvider';
import BraintreeCreditCardForm, {
  BraintreeCreditCardFormHandle,
} from '../../CreditCard/Braintree/BraintreeCreditCardForm';

import styles from './RetryPaymentBraintreeModal.module.scss';
import RetryPaymentBraintreeModalError from './RetryPaymentBraintreeModalError';

type RetryPaymentBraintreeModalProps = {
  show: boolean;
  onClose: () => void;
  token: string;
  amount: number;
  orderId: string;
  currency: string;
  siteId: number;
};

const RetryPaymentBraintreeModal: React.FC<RetryPaymentBraintreeModalProps> = ({
  show,
  onClose,
  token,
  amount,
  orderId,
  currency,
  siteId,
}) => {
  const { t } = useTranslation();
  const { csrfToken } = useAppContext();

  const creditCardFormHandle = useRef<BraintreeCreditCardFormHandle>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [showErrorModal, setShowErrorModal] = useState(false);
  const handleCancel = useCallback(() => {
    onClose();
    setErrorMessage(undefined);
  }, [onClose]);

  const handleAuthorize = async () => {
    setIsLoading(true);
    setErrorMessage(undefined);

    try {
      if (!creditCardFormHandle.current) {
        captureException({ message: 'braintree_retry_form_handle_not_found' });
        setShowErrorModal(true);
        return;
      }

      const tokenizeCardResult = await creditCardFormHandle.current.tokenizeCard();
      if (!tokenizeCardResult.success) {
        setErrorMessage(tokenizeCardResult.errorMessage);
        return;
      }

      const { binData, nonce } = tokenizeCardResult.data;
      const authorizeCardResult = await authorizePayment({
        t,
        token,
        bin: binData as string,
        nonce,
        amount,
      });

      if (!authorizeCardResult.success) {
        setErrorMessage(authorizeCardResult.errorMessage);
        return;
      }

      try {
        const response = await initiateCreditCardPayment({
          csrfToken: csrfToken as string,
          paymentAmount: removeDecimal(amount),
          paymentMethodNonce: authorizeCardResult.nonce,
          orderId,
          siteId,
          currency,
          connector: 'BRAINTREE',
          deviceData: authorizeCardResult.deviceData,
        });
        const retryPaymentStatus = response.data;
        console.log('Response from initiate payment is :', response);

        switch (retryPaymentStatus) {
          case 'SUCCESSFUL':
            onClose();
            console.log('Redirecting to order success page...');
            window.location.replace(
              window.location.origin + window.location.pathname + '?auth=SUCCESSFUL',
            );
            break;
          case 'UNSUCCESSFUL':
          case 'CANCELLED':
            console.log('Showing retry status...');
            setErrorMessage(t('checkout.payment.creditcard.error.authorize.retry'));
            break;
          default:
            // Redirect to order overview page
            console.log('Redirecting to order overview page...');
            window.location.replace(window.location.origin + window.location.pathname);
            break;
        }
      } catch (error) {
        console.error('Error in create transaction', error);
        setShowErrorModal(true);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFatalError = useCallback(() => {
    onClose();
  }, [onClose]);

  return (
    <>
      <ModalWrapper
        size="md"
        isShown={show && !showErrorModal}
        onClose={handleCancel}
        heading={t('checkout.payment.by.card')}
        headingId="retry-payment-card"
        closeBtnAriaLabel="Close"
        data-zta="retry-payment-modal">
        <ModalBody>
          {show && !showErrorModal && (
            <BraintreeCreditCardForm
              token={token}
              error={errorMessage}
              ref={creditCardFormHandle}
              containerType="default"
              labelsInline={false}
              allowVaulting={false}
              onFatalError={handleFatalError}
            />
          )}
        </ModalBody>
        <ModalFooter>
          <ModalSecondaryButton
            className={styles.modalCancel}
            onClick={handleCancel}
            disabled={isLoading}
            data-zta="closeModalBtnBottom">
            {t('general.button.back')}
          </ModalSecondaryButton>
          <ModalPrimaryButton
            onClick={handleAuthorize}
            disabled={isLoading}
            isLoading={isLoading}
            loadingIconType="paw"
            data-zta="retryPaymentSubmitBtn">
            {t('checkout.payment.pay')}
          </ModalPrimaryButton>
        </ModalFooter>
      </ModalWrapper>
      <RetryPaymentBraintreeModalError show={showErrorModal} />
    </>
  );
};

export default RetryPaymentBraintreeModal;
